import type { ProductDto, ProductStatus, PromotionSchemeDto } from '@cuito/types';
import { apiClient } from '../lib/apiClient';

/**
 * 契约 §1.3 的标准分页包裹。
 *
 * 五个字段一个不少：`totalPages` 是服务端算好的（`Math.ceil(total / pageSize)`），
 * 前端不再自行推导——两处各算一次迟早会漂移（计划全局规则 3）。
 */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * 商品与促销方案的 API 调用（`04-api-contract.md` §8）。
 *
 * 复用 `../lib/apiClient` 的**全局单例**：`createApiClient` 内部持有 401 单飞刷新的
 * `refreshPromise`，各模块自建实例会让并发刷新互相踩踏（见 `lib/apiClient.ts` 注释）。
 *
 * 请求体一律用 `Record<string, unknown>`：字段的**权威校验在服务端 DTO**
 * （`apps/api/src/modules/products/dto/*`），前端再抄一份会制造第二份会漂移的事实来源。
 */
export const catalogApi = {
  listProducts: (params: { status?: ProductStatus; keyword?: string; page?: number; pageSize?: number } = {}) =>
    apiClient.get<Paginated<ProductDto>>('/products', { params }),

  getProduct: (id: string) => apiClient.get<ProductDto>(`/products/${id}`),

  createProduct: (body: Record<string, unknown>) => apiClient.post<ProductDto>('/products', body),

  updateProduct: (id: string, body: Record<string, unknown>) =>
    apiClient.patch<ProductDto>(`/products/${id}`, body),

  /** 上架/下架（契约 §8.1 第 5 行）。商品是**下架**而不是删除，故列表操作列用它 */
  setProductStatus: (id: string, status: ProductStatus) =>
    apiClient.patch<ProductDto>(`/products/${id}/status`, { status }),

  deleteProduct: (id: string) => apiClient.del<{ success: true }>(`/products/${id}`),

  listSchemes: (params: { active?: boolean; keyword?: string; page?: number; pageSize?: number } = {}) =>
    apiClient.get<Paginated<PromotionSchemeDto>>('/promotion-schemes', { params }),

  createScheme: (body: Record<string, unknown>) =>
    apiClient.post<PromotionSchemeDto>('/promotion-schemes', body),

  updateScheme: (id: string, body: Record<string, unknown>) =>
    apiClient.patch<PromotionSchemeDto>(`/promotion-schemes/${id}`, body),

  setSchemeStatus: (id: string, active: boolean) =>
    apiClient.patch<{ id: string; active: boolean }>(`/promotion-schemes/${id}/status`, { active }),

  deleteScheme: (id: string) => apiClient.del<{ success: true }>(`/promotion-schemes/${id}`),
};
